// Breadcrumb — 현재 그룹의 조상 경로 표시 컴포넌트.
// useGroupAncestors 훅으로 조상 ID 배열 조회, useGroupsTree로 이름 조회.
// 루트 > 조상1 > 조상2 > 현재그룹 형태로 표시.

import { useGroupAncestors, useGroupsTree } from '../hooks/useGroupsTree';
import type { GroupTreeNode } from '../api/groupsTree';

interface BreadcrumbProps {
  /** 현재 선택된 그룹 ID. null이면 루트("전체") 표시. */
  groupId: number | null;
  /** 그룹 클릭 핸들러 */
  onGroupClick?: (id: number | null) => void;
}

/**
 * 트리 노드 목록에서 ID로 이름을 찾는다.
 */
function findGroupName(nodes: GroupTreeNode[], id: number): string | null {
  for (const node of nodes) {
    if (node.id === id) return node.name;
    const childResult = findGroupName(node.children, id);
    if (childResult !== null) return childResult;
  }
  return null;
}

export function Breadcrumb({ groupId, onGroupClick }: BreadcrumbProps) {
  // 조상 ID 배열 조회 (루트까지, 자신 제외)
  const { data: ancestorIds = [] } = useGroupAncestors(groupId);
  // 전체 트리 — 이름 조회용
  const { data: tree = [] } = useGroupsTree();

  // 빵부스러기 항목 구성: [전체] > [조상1] > [조상2] > [현재그룹]
  const crumbs: { id: number | null; label: string }[] = [
    { id: null, label: '전체' },
  ];

  // 조상 ID 배열은 루트부터 순서 (서버 응답 기준)
  for (const id of ancestorIds) {
    const name = findGroupName(tree, id) ?? `그룹 ${id}`;
    crumbs.push({ id, label: name });
  }

  // 현재 그룹
  if (groupId !== null) {
    const name = findGroupName(tree, groupId) ?? `그룹 ${groupId}`;
    crumbs.push({ id: groupId, label: name });
  }

  // 루트("전체")만 있으면 빵부스러기 표시 불필요
  if (crumbs.length <= 1) return null;

  return (
    <nav className="breadcrumb" aria-label="그룹 경로">
      <ol className="breadcrumb__list">
        {crumbs.map((crumb, idx) => {
          const isLast = idx === crumbs.length - 1;
          return (
            <li key={crumb.id ?? 'root'} className="breadcrumb__item">
              {isLast ? (
                <span className="breadcrumb__current" aria-current="page">
                  {crumb.label}
                </span>
              ) : (
                <>
                  <button
                    type="button"
                    className="breadcrumb__link"
                    onClick={() => onGroupClick?.(crumb.id)}
                  >
                    {crumb.label}
                  </button>
                  <span className="breadcrumb__sep" aria-hidden="true">/</span>
                </>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
